import { inject, ref } from 'vue'
import { clearNovelRecovery } from '@drama/novel-authoring/vue'

const account = ref(null)
const loading = ref(false)
const sending_code = ref(false)
const error = ref('')
let profileRequest = null
let profileAccessToken = ''

export function ensureStudioProfile(api) {
  const accessToken = api.tokenStore.getAccessToken()
  if (!accessToken) throw new Error('Studio access token is required')
  if (account.value && profileAccessToken === accessToken) return Promise.resolve(account.value)
  if (profileRequest?.accessToken === accessToken) return profileRequest.promise

  const promise = api.account.getProfile()
    .then(profile => {
      if (api.tokenStore.getAccessToken() === accessToken) {
        account.value = profile
        profileAccessToken = accessToken
      }
      return profile
    })
    .finally(() => {
      if (profileRequest?.promise === promise) profileRequest = null
    })
  profileRequest = { accessToken, promise }
  return promise
}

function clearProfile() {
  clearNovelRecovery({ account_id: account.value?.id })
  profileRequest = null
  profileAccessToken = ''
  account.value = null
}

export function useStudioAuth() {
  const api = inject('api')
  if (!api) throw new Error('useStudioAuth: api provider is missing')

  async function loadProfile() {
    if (!api.tokenStore.getAccessToken()) {
      if (!api.tokenStore.getRefreshToken()) {
        account.value = null
        return null
      }
      try {
        await api.tokenStore.refresh()
      } catch (err) {
        error.value = err?.message || 'REFRESH_FAILED'
        account.value = null
        return null
      }
    }
    loading.value = true
    error.value = ''
    try {
      account.value = await ensureStudioProfile(api)
      return account.value
    } catch (err) {
      error.value = err?.message || 'PROFILE_FAILED'
      clearProfile()
      return null
    } finally {
      loading.value = false
    }
  }

  function acceptSession(session) {
    if (account.value?.id && account.value.id !== session.account.id) clearNovelRecovery({ account_id: account.value.id })
    api.tokenStore.setTokens(session)
    profileRequest = null
    profileAccessToken = ''
    account.value = session.account
    return session.account
  }

  async function authenticate(request) {
    loading.value = true
    error.value = ''
    try {
      return acceptSession(await request())
    } catch (err) {
      error.value = err?.message || 'LOGIN_FAILED'
      throw err
    } finally {
      loading.value = false
    }
  }

  function login({ email, password }) {
    return authenticate(() => api.auth.login({ email, password }))
  }

  function loginWithEmailCode({ email, verification_code, locale }) {
    return authenticate(() => api.auth.loginWithEmailCode({ email, verification_code, locale, device_id: api.tokenStore.getDeviceId() }))
  }

  async function sendLoginCode({ email, locale }) {
    sending_code.value = true
    error.value = ''
    try {
      return await api.auth.sendCode({ channel: 'email', target: email, purpose: 'login', locale })
    } catch (err) {
      error.value = err?.message || 'SEND_CODE_FAILED'
      throw err
    } finally {
      sending_code.value = false
    }
  }

  async function logout() {
    const refreshToken = api.tokenStore.getRefreshToken()
    try {
      if (refreshToken) await api.auth.logout({ refresh_token: refreshToken })
    } finally {
      api.tokenStore.clear()
      clearProfile()
    }
  }

  return { account, loading, sending_code, error, loadProfile, login, loginWithEmailCode, sendLoginCode, logout }
}
